import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Box, IconButton, Typography, Drawer, Divider, Stack, Chip, Skeleton } from '@mui/material';
import type { GetApprovalFlowInvoicesListResponse } from '@vy/api-definition';
import { isNotEmptyArray, isNotNullOrUndefined, isNullOrUndefined, type ItemOf } from '@vy/utils';
import { useMemo } from 'react';
import { getReviewStatusLabel } from '../../InvoicesPage/invoices-page-components/InvoiceOverviewTable/invoice-overview-table-consts/invoice-columns';
import { InvoiceNavigationArrows } from './InvoiceNavigationArrows';

type ApprovalFlowHeaderProps = {
  isCollapseOpen: boolean;
  handleToggle: () => void;
  selectedInvoiceId?: string;
  drawerWidth?: number;
  side?: 'left' | 'top' | 'right' | 'bottom';
  children?: React.ReactNode;
  invoiceData?: GetApprovalFlowInvoicesListResponse;
  onInvoiceChange?: (newInvoice: ItemOf<GetApprovalFlowInvoicesListResponse>) => void;
};
export const NOT_FOUND_INDEX = -1;

export const ApprovalFlowHeader: React.FC<ApprovalFlowHeaderProps> = ({
  isCollapseOpen,
  handleToggle,
  invoiceData,
  selectedInvoiceId,
  onInvoiceChange,
  drawerWidth = 354,
  side = 'right',
  children,
}) => {
  const invoicePosition = useMemo(
    () => invoiceData?.findIndex((invoice) => invoice.invoiceId === selectedInvoiceId),
    [invoiceData, selectedInvoiceId],
  );

  const selectedInvoice = getSelectedInvoice(invoiceData, invoicePosition);

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "center",
          marginLeft: "25px"
        }}>
        {isNullOrUndefined(selectedInvoice) ? (
          <Skeleton variant="text" sx={{ fontSize: '2rem', width: '200px' }} />
        ) : (
          <Typography variant="h5" noWrap sx={{
            alignItems: "center"
          }}>
            {selectedInvoice.customerName}
          </Typography>
        )}

        {isNotNullOrUndefined(selectedInvoice) && (
          <Chip size="small" variant="outlined" label={getReviewStatusLabel(selectedInvoice?.reviewStatus)} />
        )}
      </Stack>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "center",
          justifyContent: "flex-end",
          width: '100%',
          position: 'absolute',
          right: isCollapseOpen ? '370px' : '80px !important'
        }}>
        <Chip
          size="medium"
          label={
            isValidInvoicePosition(invoicePosition) &&
            isNotEmptyArray(invoiceData) &&
            isNotNullOrUndefined(invoicePosition)
              ? `${invoicePosition + 1}/${invoiceData.length}`
              : `0/${invoiceData?.length ?? 0}`
          }
        />

        <InvoiceNavigationArrows
          onInvoiceChange={onInvoiceChange}
          invoiceData={invoiceData}
          invoicePosition={invoicePosition}
        />

        <Box
          sx={{
            height: '100%',
            width: '64px',
            display: 'flex',
            WebkitFlexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'white',
            border: '1',
            top: '58px',
            zIndex: '1200',
            position: 'fixed',
            right: '0',
            borderLeft: '1px solid #0D1B271F',
          }}
        >
          <IconButton
            edge="end"
            onClick={handleToggle}
            sx={{
              ...(isCollapseOpen && { display: 'none' }),
              padding: '8px',
              marginRight: '2px',
              marginTop: '16px',
            }}
          >
            <KeyboardDoubleArrowLeftIcon />
          </IconButton>
        </Box>
      </Stack>
      <Drawer
        sx={{
          width: drawerWidth,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            top: '58px',
          },
        }}
        variant="persistent"
        anchor={side}
        open={isCollapseOpen}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 1,
            right: 0,
            padding: 2,
          }}
        >
          <IconButton onClick={handleToggle}>
            <KeyboardDoubleArrowRightIcon />
          </IconButton>
        </Box>
        {children}
      </Drawer>
      <Divider />
    </>
  );
};

export function isValidInvoicePosition(invoicePosition: number | undefined) {
  return invoicePosition !== NOT_FOUND_INDEX && isNotNullOrUndefined(invoicePosition);
}

export function getSelectedInvoice(
  invoiceData: GetApprovalFlowInvoicesListResponse | undefined,
  invoicePosition?: number,
) {
  return isNotEmptyArray(invoiceData) &&
    isValidInvoicePosition(invoicePosition) &&
    isNotNullOrUndefined(invoicePosition)
    ? invoiceData[invoicePosition]
    : undefined;
}
